import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { config } from '@/config/env';

export default function ExpiredPasswordPage() {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();

  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [policyDesc, setPolicyDesc] = useState('');

  // Redirect se non c'è token temporaneo
  useEffect(() => {
    if (!sessionStorage.getItem('tempToken')) {
      navigate('/login');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess(''); setPolicyDesc('');

    if (!oldPwd.trim()) { setError(t('expiredOldRequired')); return; }
    if (!newPwd.trim()) { setError(t('expiredNewRequired')); return; }
    if (!confirmPwd.trim()) { setError(t('expiredConfirmRequired')); return; }
    if (newPwd !== confirmPwd) { setError(tc('changePwd.mismatch')); return; }

    setLoading(true);
    try {
      const tempToken = sessionStorage.getItem('tempToken');
      const url = config.WSREST_TABATA.replace(/\/+$/, '') + '/change-password';
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + tempToken,
        },
        body: JSON.stringify({ old_password: oldPwd, new_password: newPwd }),
      });

      if (res.status === 401) {
        sessionStorage.removeItem('tempToken');
        sessionStorage.removeItem('tempTokenExp');
        setError(t('expiredTokenExpired'));
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      if (!res.ok) {
        const text = await res.text();
        const errData = text ? JSON.parse(text) : null;
        setError(errData?.error || t('expiredError'));
        if (errData?.policy_description) {
          setPolicyDesc(errData.policy_description);
        }
        return;
      }

      const data = await res.json();
      sessionStorage.removeItem('tempToken');
      sessionStorage.removeItem('tempTokenExp');

      // Dopo cambio password: salva nuovi token e vai al dashboard
      if (data?.t) {
        sessionStorage.setItem('mT', data.t);
        if (data.tr) sessionStorage.setItem('mR', data.tr);
        if (data.tex) sessionStorage.setItem('mEx', data.tex);
        setSuccess(data?.message || t('expiredSuccess'));
        setTimeout(() => navigate('/visite/nuova'), 1500);
      } else {
        setSuccess(data?.message || t('expiredSuccess'));
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch {
      setError(t('expiredError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-lg-flex min-vh-100">
      <div className="d-flex flex-column py-4" style={{ width: '100%', maxWidth: 520, minWidth: 320, paddingLeft: 40, paddingRight: 40 }}>
        <div className="d-flex flex-column justify-content-center flex-grow-1">
          <div className="mb-4 pb-2">
            <img src="/images/logo.png" alt="LMeet" style={{ height: 56 }} />
          </div>

          <h1 className="h3 mb-2">{t('expiredTitle')}</h1>
          <p className="text-muted mb-4" style={{ fontSize: 13 }}>{t('expiredDesc')}</p>

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3">
              <Form.Label className="text-xs">{tc('changePwd.oldPassword')}</Form.Label>
              <InputGroup>
                <Form.Control type={showOld ? 'text' : 'password'} value={oldPwd}
                  onChange={e => { setOldPwd(e.target.value); setError(''); }} autoComplete="off" />
                <Button variant="outline-secondary" onClick={() => setShowOld(!showOld)} tabIndex={-1}>
                  {showOld ? <FiEyeOff /> : <FiEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="text-xs">{tc('changePwd.newPassword')}</Form.Label>
              <InputGroup>
                <Form.Control type={showNew ? 'text' : 'password'} value={newPwd}
                  onChange={e => { setNewPwd(e.target.value); setError(''); }} autoComplete="off" />
                <Button variant="outline-secondary" onClick={() => setShowNew(!showNew)} tabIndex={-1}>
                  {showNew ? <FiEyeOff /> : <FiEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="text-xs">{tc('changePwd.confirmPassword')}</Form.Label>
              <Form.Control type="password" value={confirmPwd}
                onChange={e => { setConfirmPwd(e.target.value); setError(''); }} autoComplete="off" />
              {confirmPwd && newPwd !== confirmPwd && (
                <Form.Text className="text-danger" style={{ fontSize: 11 }}>{tc('changePwd.mismatch')}</Form.Text>
              )}
            </Form.Group>

            {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
            {policyDesc && <Alert variant="warning" className="py-2" style={{ fontSize: 12 }}>{policyDesc}</Alert>}
            {success && <Alert variant="success" className="py-2" style={{ fontSize: 13 }}>{success}</Alert>}

            <Button type="submit" className="w-100 login-btn btn-lg" disabled={loading || !!success}>
              {loading && <span className="spinner-border spinner-border-sm me-2" />}
              {tc('changePwd.title')}
            </Button>
          </Form>
        </div>

        <div className="pt-4">
          <small className="text-muted">
            &copy; {new Date().getFullYear()} Partner Tecnologico{' '}
            <a href="https://www.tabatatech.it" target="_blank" rel="noreferrer">TABATA</a>
          </small>
        </div>
      </div>

      <div className="d-none d-lg-block flex-grow-1 position-relative" style={{ minHeight: '100vh' }}>
        <img src="/images/banner_login.png" alt="" className="position-absolute top-0 start-0 w-100 h-100" style={{ objectFit: 'cover' }} />
      </div>
    </div>
  );
}
